import React, { useEffect, useRef, useState } from "react";
import TopMostPortal from "../../../../components/ui/utils/Portals";
import Select from "react-select";
import { SelectOption } from "../ErrorResolutionPanel";
import { logDebug, logError } from "../../../../shared/common/logger";
import RestAPI from "../../../../utils/restAPI";
import {
	EDrywallWidthCalculation,
	IDatabaseLineItem,
	IDrywallWidthType,
	ILineItemForUI,
	ITupleOutputForUI,
	LineItemElementProps,
} from "../../../../shared/common/EstimateEngine/EstimateEngineTypes";
import { getEstimaticsCurrentProjectStore } from "../../../../data/DataStores";
import SharedUtils from "../../../../shared/common/SharedUtils";
import EngineUtils from "../../../../shared/common/EngineUtils";
import { v4 as uuidv4 } from "uuid";
import { getProcessedEstimatesStore } from "../../../../data/ProcessedEstimatesStore";
import { observer } from "mobx-react";
import { toastNotify } from "../../../../shared/client/FrontendUtils";
import { ImageCanvas } from "../../../../components/ui/utils/ImageCropping";

const customStyles = {
	control: (provided: any) => ({
		...provided,
		whiteSpace: "nowrap",
	}),
	valueContainer: (provided: any) => ({
		...provided,
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	}),
	singleValue: (provided: any) => ({
		...provided,
		whiteSpace: "nowrap",
	}),
	placeholder: (provided: any) => ({
		...provided,
		whiteSpace: "nowrap",
	}),
	option: (provided: any, state: any) => ({
		...provided,
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	}),
};

const LineEditingDialog: React.FC<{ pageId: string; roomName?: string; tuplesPageID: string }> = observer(
	({ pageId, roomName, tuplesPageID }) => {
		const currentProjectStore = getEstimaticsCurrentProjectStore();
		const processedEstimatesStore = getProcessedEstimatesStore();
		const allPossibleCategories = useRef(Object.keys(TicType1CatToCellMap).map((category) => ({ value: category, label: category })));
		const [availableCells, setAvailableCells] = useState<SelectOption[]>(Object.values(TicType1CatToCellMap).flat());
		const [selectedCategory, setSelectedCategory] = useState<SelectOption | null>();
		const [selectedCell, setSelectedCell] = useState<SelectOption | null>();
		const [lineItemsElements, setLineItemsElements] = useState<
			{
				elements: LineItemElementProps[];
				lineItemId: string;
			}[]
		>([]);
		const [selectedElements, setSelectedElements] = useState<LineItemElementProps[] | undefined>();
		const [selectedLineItemId, setSelectedLineItemId] = useState("");
		const [showSetQuantity, setShowSetQuantity] = useState(false);
		const [currentTupleId, setCurrentTupleId] = useState<string | null>(null);
		const [quantity, setQuantity] = useState<{ quantity: number; measurement: string } | null>(null);
		const [search, setSearch] = useState("");
		const [searchResults, setSearchResults] = useState<{ id: string; str: string }[]>([]);
		const [editingFreeForm, setEditingFreeForm] = useState(false);

		const freeFormIdOptions = ["free_form", "_empty", "margin_"];

		async function getLineItemElementsByTupleId(tid: string) {
			currentProjectStore.showLoadingAnimation = true;
			if (freeFormIdOptions.some((option) => tid.includes(option))) {
				logDebug("free form tuple", tid);
				currentProjectStore.showLoadingAnimation = false;
				setSelectedElements(undefined);
				return;
			}
			try {
				const url = RestAPI.getServerBaseUrl(`/rest/estimates/getLineItemElements?tupleId=${tid}`);
				const elements = (await RestAPI.fetchURL(url, "GET")) as {
					elements: LineItemElementProps[];
					lineItemId: string;
				}[];
				await SharedUtils.sleep(200);
				setLineItemsElements(elements);
				logDebug("elements", elements);
				if (elements.length === 1) {
					setSelectedElements(elements[0].elements);
					setSelectedLineItemId(elements[0].lineItemId);
				}
				// logDebug("elements", elements);
			} catch (err) { }
			currentProjectStore.showLoadingAnimation = false;
		}
		async function getLineItemElementsByLineitemId(tid: string) {
			currentProjectStore.showLoadingAnimation = true;
			if (freeFormIdOptions.some((option) => tid.includes(option))) {
				logDebug("free form line item", tid);
				currentProjectStore.showLoadingAnimation = false;
				setSelectedElements(undefined);
				return;
			}
			try {
				const url = RestAPI.getServerBaseUrl(`/rest/estimates/getLineItemElements?lineItemId=${tid}`);
				const elements = (await RestAPI.fetchURL(url, "GET")) as {
					elements: LineItemElementProps[];
					lineItemId: string;
				}[];
				// logDebug("elements", tid, elements);
				await SharedUtils.sleep(200);
				setLineItemsElements(elements);
				setSelectedElements(elements[0].elements);
				setSelectedLineItemId(elements[0].lineItemId);
			} catch (err) { }
			currentProjectStore.showLoadingAnimation = false;
		}
		async function fetchCategories() {
			try {
				const res = await RestAPI.fetchAPI("/rest/estimates/searchLineItems", "GET", {
					searchString: search,
					categories: [].join(","),
					returnIds: true,
				});
				setSearchResults([...res]);
				// logDebug("res", res);
			} catch (err) { }
		}

		useEffect(() => {
			if (processedEstimatesStore.editingLineItemTuple) {
				// we are editing an existing line item. check and see if we can pull the elements and present them to the user
				const li = processedEstimatesStore.editingLineItemTuple.lineItems[processedEstimatesStore.editingLineItemIndex];

				if (processedEstimatesStore.editingLineItemTuple.isFreeFormFlag) {
					setEditingFreeForm(true);
				}

				if (li.lineItemId) {
					getLineItemElementsByLineitemId(li.lineItemId);
				} else if (processedEstimatesStore.editingLineItemTuple.tupleId) {
					const cell = Object.values(TicType1CatToCellMap)
						.flat()
						.find((cell) => cell.value === processedEstimatesStore.editingLineItemTuple!.tupleId);
					if (cell) {
						Object.keys(TicType1CatToCellMap).forEach((category) => {
							const categoryCells = TicType1CatToCellMap[category];
							if (categoryCells.includes(cell)) {
								setSelectedCategory({ value: category, label: category });
							}
						});
						setTimeout(() => {
							setSelectedCell(cell);
							getLineItemElementsByTupleId(processedEstimatesStore.editingLineItemTuple!.tupleId);
						}, 100);
					}
				}
			}
		}, []);

		useEffect(() => {
			if (search) {
				fetchCategories();
			} else {
				setSearchResults([]);
			}
		}, [search]);

		useEffect(() => {
			if (selectedCategory?.value) {
				setAvailableCells(TicType1CatToCellMap[selectedCategory.value]);
			} else {
				setAvailableCells(Object.values(TicType1CatToCellMap).flat());
				setSelectedElements(undefined);
			}
			setSelectedCell(null);
		}, [selectedCategory]);

		useEffect(() => {
			if (selectedCell?.value) {
				setCurrentTupleId(selectedCell.value);
				getLineItemElementsByTupleId(selectedCell.value);
			} else {
				setCurrentTupleId(null);
				setLineItemsElements([]);
			}
		}, [selectedCell]);

		// useEffect(() => {
		// 	logDebug("selectedLineItemId", selectedLineItemId);
		// }, [selectedLineItemId]);

		return (
			<div className="w-[1026px] h-[807px] bg-white rounded-xl shadow flex-col justify-start items-start inline-flex">
				{/* {[0].map((_) => {
							logDebug("editingLineItemTuple", processedEstimatesStore.editingLineItemTuple);
							return <></>;
						})} */}
				<div
					className="relative w-full flex justify-end btn"
					onClick={() => {
						setSelectedElements(undefined);
						setShowSetQuantity(false);
						setSelectedCategory(null);
						setLineItemsElements([]);
						setCurrentTupleId(null);
						processedEstimatesStore.showAddLineItemPanelForPageId = "";
					}}
				>
					<img src="/assets/ui/x-close.svg" className="w-5 h-5 mr-2 mt-2" />
				</div>
				<div className="self-stretch h-[73px] border-b border-[#344053] flex-col justify-start items-center flex">
					<div className="self-stretch px-6 pt-6 bg-white justify-start items-start gap-4 inline-flex">
						<div className="grow shrink basis-0 flex-col justify-start items-start gap-1 inline-flex">
							<div className="self-stretch text-[#0f1728] text-xl font-semibold font-['Hanken Grotesk'] leading-7">
								{processedEstimatesStore.editingTupleIndex === -1 ? "Add new line to" : "Edit line in"}{" "}
								<strong>{!roomName?.trim() || roomName?.toLowerCase() === "n/a" ? "Unnamed room" : roomName}</strong>
							</div>
						</div>
					</div>
				</div>
				<div className="self-stretch grow shrink basis-0 py-4 flex-col justify-start items-center gap-2 flex">
					<div className="self-stretch h-[552px] bg-white rounded-2xl flex-col justify-start items-start flex">
						{!selectedElements && (
							<>
								<div className="self-stretch px-6 py-5 border-b border-[#eaecf0] justify-between items-center inline-flex">
									<div className="text-justify text-[#0f1728] text-base font-semibold font-['Hanken Grotesk'] leading-snug">
										Select a line from the list ({lineItemsElements?.length})
									</div>
									<div className="justify-end items-center gap-3 flex">
										<Select
											isClearable
											value={selectedCategory}
											onChange={(e) => {
												setSelectedCategory(e);
												setSearch("");
											}}
											placeholder="Select category"
											className="w-[198px]"
											options={allPossibleCategories.current}
											styles={customStyles}
										></Select>
										<Select
											isClearable
											value={selectedCell}
											onChange={(e) => {
												setSelectedCell(e);
												setSearch("");
											}}
											placeholder="Select cell"
											styles={customStyles}
											className="w-[198px]"
											options={availableCells}
										></Select>
										<input
											placeholder="Search"
											className="w-[198px] rounded h-9 focus:outline-none border px-2 "
											value={search}
											onChange={(e) => {
												setSelectedCategory(null);
												setSelectedCell(null);
												setSearch(e.target.value);
											}}
										/>
									</div>
								</div>
								<div className="h-[500px] overflow-y-auto w-full">
									{!!lineItemsElements?.length &&
										lineItemsElements.map((elements, index) => {
											return (
												<LineItemElement
													elements={elements.elements}
													lineIndex={index}
													key={index}
													onSelect={(selectedElements) => {
														setSelectedLineItemId(elements.lineItemId);
														setSelectedElements(selectedElements);
													}}
												/>
											);
										})}
									{!lineItemsElements?.length &&
										!!searchResults.length &&
										searchResults.map((result, lineIndex) => {
											return (
												<div
													className={` btn self-stretch w-full h-20 ${lineIndex % 2 ? "bg-gray-50" : "bg-white"
														} border-l border-r border-b border-[#eaecf0] justify-between items-center inline-flex`}
													onClick={async () => {
														currentProjectStore.showLoadingAnimation = true;
														try {
															const li = (await RestAPI.fetchAPI("/rest/estimates/getLineItemById", "GET", {
																id: result.id,
															})) as IDatabaseLineItem;
															setSelectedElements([...li.elements]);
															setSelectedLineItemId(li.id);
															setSearch("");
															// logDebug("li", li, li.elements);
														} catch (err) {
															logError("err", err);
														}
														currentProjectStore.showLoadingAnimation = false;
													}}
												>
													<div className="grow shrink basis-0 self-stretch justify-start items-center flex">
														<div className="w-[691px] self-stretch px-8 justify-start items-center gap-2 flex ">
															<div className="rounded justify-start items-center flex whitespace-nowrap">
																<div className="text-justify text-[#101828] text-lg font-normal font-['Hanken Grotesk'] ">
																	{result.str}
																</div>
															</div>
														</div>
													</div>
												</div>
											);
										})}
									{editingFreeForm && processedEstimatesStore.fullPageImagePath && (
										<div className="w-full flex flex-col px-6 py-4">
											<div className="text-base font-semibold font-['Hanken Grotesk']">Free Form Cell:</div>
											<div className="flex justify-center mt-4">
												<ImageCanvas
													className={"h-32"}
													url={RestAPI.getStorageFileUrl(processedEstimatesStore.fullPageImagePath)}
													rect={processedEstimatesStore.editingLineItemTuple!.tupleRegion}
													paddingPixels={60}
												/>
											</div>
										</div>
									)}
									{!lineItemsElements?.length && !searchResults.length && !editingFreeForm && (
										<div className="h-[323px] w-full pt-14 flex-col justify-center items-center gap-[23px] inline-flex">
											<div className="w-32 h-32 pl-[13.09px] pr-[13.47px] pt-[17.63px] pb-[19.09px] justify-center items-center inline-flex">
												<img src="/assets/ui/addLineEmpty.svg" alt="add line empty" />
											</div>
											<div className="text-[#0f1728] text-xl font-semibold font-['Hanken Grotesk'] leading-7">
												No results to show
											</div>
											<div className="text-[#0f1728] text-xs font-normal font-['Hanken Grotesk'] leading-none">
												Try selecting a cell or resetting filtering to try again
											</div>
											<div
												className="justify-start items-center gap-3 inline-flex btn"
												onClick={() => {
													setSelectedCategory(null);
													setSelectedCell(null);
												}}
											>
												<div className="justify-start items-center gap-2 flex">
													<div className="text-[#194084] text-lg font-semibold font-['Hanken Grotesk'] leading-[25.20px]">
														Reset
													</div>
												</div>
											</div>
										</div>
									)}
								</div>
							</>
						)}
						{!!selectedElements && !showSetQuantity && (
							<LineItemBuilder
								elements={selectedElements}
								onChange={(changedElements) => {
									// logDebug("changedElements", changedElements, selectedElements);
									setSelectedElements([...changedElements]);
								}}
							/>
						)}
						{!!selectedElements && showSetQuantity && (
							<SetQuantityPanel
								elements={selectedElements}
								currentTupleId={currentTupleId!}
								onSelect={(qnt) => {
									setQuantity({ ...qnt });
								}}
							/>
						)}
					</div>
					<div className="self-stretch px-4 pt-8 rounded-2xl border-[#eff8ff] justify-start items-center gap-1.5 inline-flex">
						<div className="w-6 h-6 relative" />
						<div>
							<span className="text-[#667084] text-sm font-medium font-['Hanken Grotesk'] leading-tight">
								Need help finding the correct line item,{" "}
							</span>
							<span className="text-[#156fee] text-sm font-bold font-['Hanken Grotesk'] underline leading-tight">
								Click here
							</span>
						</div>
					</div>
				</div>
				{!selectedElements && (
					<div className="self-stretch px-6 pt-4 pb-6 bg-white border-[#eaecf0] justify-center items-center gap-6 inline-flex">
						<div
							className="justify-end items-center gap-6 flex btn"
							onClick={() => {
								setSelectedElements(undefined);
								setShowSetQuantity(false);
								processedEstimatesStore.showAddLineItemPanelForPageId = "";
								setSelectedCategory(null);
								setLineItemsElements([]);
								setCurrentTupleId(null);
							}}
						>
							<div className="px-4 py-3 bg-white rounded-lg border border-[#eaecf0] justify-center items-center gap-2 flex">
								<div className="justify-start items-center gap-2 flex">
									<div className="text-[#344053] text-base font-semibold font-['Hanken Grotesk'] leading-snug">
										Cancel
									</div>
								</div>
							</div>
						</div>
					</div>
				)}
				{!!selectedElements && (
					<div className="h-[86px] w-full px-6 pt-4 pb-6 bg-white border-[#eaecf0] justify-between items-center inline-flex ">
						<div
							className="justify-end items-center gap-6 flex btn"
							onClick={() => {
								if (showSetQuantity) {
									setShowSetQuantity(false);
								} else {
									if (!selectedCell?.value) {
										setSelectedCategory(null);
										setSelectedCell(null);
										setLineItemsElements([]);
									}
									setSelectedElements(undefined);
									setSelectedLineItemId("");
								}
							}}
						>
							<div className="px-4 py-3 bg-white rounded-lg border border-[#eaecf0] justify-center items-center gap-2 flex">
								<div className="justify-start items-center gap-2 flex">
									<div className="text-[#344053] text-base font-semibold font-['Hanken Grotesk'] leading-snug">Back</div>
								</div>
							</div>
						</div>
						<div
							className="justify-end items-center gap-6 flex btn"
							onClick={async () => {
								if (showSetQuantity) {
									currentProjectStore.showLoadingAnimation = true;
									const orderIndex = await RestAPI.fetchAPI("/rest/estimates/getLineItemOrderIndex", "GET", {
										lineItemId: selectedLineItemId!,
									});
									const li: ILineItemForUI = {
										leftMapping: [],
										rightMapping: [],
										mappingExtraData: [],
										qnm: quantity ? [quantity] : [],
										lineItemId: selectedLineItemId,
										lineItem: getLineItemFromElements2(selectedElements),
										confidence: 1, // manually added line items are 100% confident
										orderIndex,
									};
									logDebug(li.lineItemId, li.lineItem);
									if (getProcessedEstimatesStore().editingLineItemTuple) {
										const editingTuple = getProcessedEstimatesStore().editingLineItemTuple!;
										const editintLine = editingTuple.lineItems[getProcessedEstimatesStore().editingLineItemIndex];
										const isTheSameLineItemId = editintLine.lineItemId === li.lineItemId;
										const editingLineIsAComplement =
											!!getProcessedEstimatesStore().editingLineItemTuple?.lineItems[
												getProcessedEstimatesStore().editingLineItemIndex
											].isComplement;
										const editingLineIsParentWithComplements =
											getProcessedEstimatesStore().editingLineItemIndex === 0 &&
											!editintLine.isComplement &&
											editingTuple.lineItems.some((li) => li.isComplement);
										// logDebug("editing", isTheSameLineItemId, editingLineIsParentWithComplements, editingLineIsAComplement);
										currentProjectStore.showLoadingAnimation = false;
										try {
											// here we first delete the old line item and then create a new one
											// TODO :: this should happen in one transactional call to the backend
											const clone = { ...getProcessedEstimatesStore().editingLineItemTuple! };
											clone.deleted = true;
											await processedEstimatesStore.updateOrCreateLineItem(
												clone.tupleId,
												currentProjectStore.estimaticsData.estimateId,
												tuplesPageID,
												clone,
											);
											clone.deleted = false;
											const tuple = getProcessedEstimatesStore().editingLineItemTuple!;
											const prefix = processedEstimatesStore.prefixOrder.find((prefix) =>
												tuple.tupleId.startsWith(prefix),
											);
											clone.tupleId = `${prefix ? prefix + "_" : ""}$user_${uuidv4()}`;
											// if selectedCell?.value is not null then we are creating a new line item from user selection of category and cell
											li.addedByTPARule =
												selectedCell?.value || !isTheSameLineItemId
													? false
													: clone.lineItems[getProcessedEstimatesStore().editingLineItemIndex].addedByTPARule;
											li.isComplement =
												selectedCell?.value || !isTheSameLineItemId ? false : editingLineIsAComplement;
											li.confirmed = true;

											clone.isFreeFormFlag = false;
											clone.lineItems[getProcessedEstimatesStore().editingLineItemIndex] = li;
											if (editingLineIsParentWithComplements && !isTheSameLineItemId) {
												// logDebug("removing complements");
												// if the user changed the type of line item we need to remove the complements
												clone.lineItems.forEach((li) => {
													if (li.isComplement) li.deleted = true;
												});
												// TODO :: we also need to add the complements of the new generated line item if needed
												// TODO :: we need to move all this logic to the server to one place and transtionally update the line item
											} else {
												// logDebug("NOT removing complements");
											}
											await processedEstimatesStore.updateOrCreateLineItem(
												clone.tupleId,
												currentProjectStore.estimaticsData.estimateId,
												tuplesPageID,
												clone,
												editingFreeForm ? "FreeFormEditing" : undefined,
											);
											processedEstimatesStore.blinkTupleLineIndex = processedEstimatesStore.editingTupleIndex;
										} catch (err) {
											toastNotify("Failed to update line item", true);
										}
									} else {
										li.confirmed = true;
										const lineItemData: ITupleOutputForUI = {
											tupleId: `${selectedCategory?.value ? SharedUtils.makeIdFromString(selectedCategory.value) + "_" : ""
												}$user_${uuidv4()}`,
											tupleRegion: {
												height: 0,
												width: 0,
												left: 0,
												top: 0,
											},
											lineItems: [li],
										};
										try {
											await processedEstimatesStore.updateOrCreateLineItem(
												lineItemData.tupleId,
												currentProjectStore.estimaticsData.estimateId,
												pageId,
												lineItemData,
											);
										} catch (err) {
											toastNotify("Failed to create line item", true);
										}
									}

									setSelectedElements(undefined);
									setShowSetQuantity(false);
									processedEstimatesStore.showAddLineItemPanelForPageId = "";
									setSelectedCategory(null);
									currentProjectStore.showLoadingAnimation = false;
								} else {
									setShowSetQuantity(true);
								}
							}}
						>
							<div className="px-4 py-3 bg-[#156fee] rounded-lg shadow border border-[#156fee] justify-center items-center gap-2 flex">
								<div className="justify-start items-center gap-2 flex">
									<div className="text-white text-base font-semibold font-['Hanken Grotesk'] leading-snug">
										{showSetQuantity ? "Save New Line" : "Next"}
									</div>
								</div>
								{!showSetQuantity && <img src="/assets/ui/arrow-right-btn.svg" className="w-5 h-5 relative" />}
							</div>
						</div>
					</div>
				)}
			</div>
		);
	},
);

export const LineItemBuilder: React.FC<{ elements: LineItemElementProps[]; onChange: (elements: LineItemElementProps[]) => void }> = ({
	elements,
	onChange,
}) => {
	const [editedElements, setEditedElements] = useState<LineItemElementProps[]>(
		elements.map((element) => ({
			...element,
			value: element.type === "text" ? element.value : element.defaultValue || (element.allowEmptyValue ? "" : element.value),
		})),
	);
	// logDebug("elements", elements, editedElements);
	return (
		<div className="h-[552px] w-full  bg-white rounded-2xl flex-col justify-start items-start inline-flex">
			<div className="self-stretch bg-white border-l border-r border-[#cfd4dc] items-center inline-flex">
				<div className="grow shrink overflow-x-auto basis-0 self-stretch px-12 py-8  gap-[88px] flex ">
					<div className="grow shrink basis-0 h-[142px] justify-start items-center gap-2 flex ">
						{editedElements.map((element, index) => {
							if (element.type === "text") {
								return (
									<div
										key={`${element.type}-${index}-${element.value}`}
										className="rounded justify-start items-center flex whitespace-nowrap"
									>
										<div className="text-justify text-[#101828] text-3xl font-normal font-['Hanken Grotesk'] ">
											{element.value}
										</div>
									</div>
								);
							}
							const value = element.value.trim();
							if (value === "" || value === "$empty") return null;
							return (
								<div
									key={`${element.type}-${index}-${element.value}`}
									className="px-2 rounded justify-start items-center  whitespace-nowrap flex bg-[#eff8ff]"
								>
									<div className="text-justify text-[#101828] text-3xl font-normal font-['Hanken Grotesk'] ">{value}</div>
								</div>
							);
						})}
					</div>
				</div>
			</div>
			<div className="self-stretch py-4 bg-white border-l border-r border-t border-[#cfd4dc] justify-between items-center inline-flex overflow-x-auto">
				{editedElements.map((element, onlyIndex) => {
					if (element.type === "text") return null;
					return (
						<div
							key={`${element.type}-${onlyIndex}-${element.value}`}
							className="grow shrink basis-0 self-stretch p-4 bg-white flex-col justify-start items-start gap-6 inline-flex border-r"
						>
							{[...(element.acceptableValues ?? []), ...(element.allowEmptyValue ? ["$empty"] : [])]?.map((value, index) => {
								if (value !== element.value) {
									return (
										<div
											key={onlyIndex * 1000 + index}
											className="self-stretch pb-0.5 justify-start items-start gap-0.5 inline-flex"
										>
											<div className="w-6 h-6 relative" />
											<div
												className="grow shrink basis-0 text-[#475466] hover:text-[#1849a9] hover:font-bold  text-base font-normal font-['Hanken Grotesk'] leading-normal btn"
												onClick={() => {
													const newElements = [...editedElements];
													newElements[onlyIndex] = { ...newElements[onlyIndex], value };
													setEditedElements(newElements);
													onChange(newElements);
												}}
											>
												{value === "$empty" ? "Empty value" : value}
											</div>
										</div>
									);
								} else {
									return (
										<div className="self-stretch pl-1 pr-7 pb-0.5 bg-[#eff8ff] rounded-lg border border-[#d1e9ff] justify-start items-center inline-flex">
											<div className="w-6 h-6 px-[5.72px] pt-[5.75px] pb-[6.27px] opacity-0 justify-center items-center flex" />
											<div
												className="grow shrink basis-0 text-[#1849a9] text-base font-bold font-['Hanken Grotesk'] leading-normal  btn"
												onClick={() => {
													const newElements = [...editedElements];
													newElements[onlyIndex] = { ...newElements[onlyIndex], value };
													setEditedElements(newElements);
													onChange(newElements);
												}}
											>
												{value === "$empty" ? "Empty value" : value}
											</div>
										</div>
									);
								}
							})}
							{/* <div className="self-stretch pb-0.5 justify-start items-start gap-0.5 inline-flex">
							<div className="w-6 h-6 relative" />
							<div className="grow shrink basis-0 text-[#475466] text-base font-normal font-['Hanken Grotesk'] leading-normal">Remove and Replace</div>
						</div>
						<div className="self-stretch pl-1 pr-7 pb-0.5 bg-[#eff8ff] rounded-lg border border-[#d1e9ff] justify-start items-center inline-flex">
							<div className="w-6 h-6 px-[5.72px] pt-[5.75px] pb-[6.27px] opacity-0 justify-center items-center flex" />
							<div className="grow shrink basis-0 text-[#1849a9] text-base font-bold font-['Hanken Grotesk'] leading-normal">remove</div>
						</div>
						<div className="self-stretch pb-0.5 justify-start items-start gap-0.5 inline-flex">
							<div className="w-6 h-6 px-[5.72px] pt-[5.75px] pb-[6.27px] opacity-0 justify-center items-center flex" />
							<div className="grow shrink basis-0 text-[#475466] text-base font-normal font-['Hanken Grotesk'] leading-normal">Empty value</div>
						</div> */}
						</div>
					);
				})}
			</div>
		</div>
	);
};



export const LineItemBuilderMini: React.FC<{
	initialValues: string[];
	title: string;
	elements: LineItemElementProps[];
	onChange: (elements: string[]) => void;
	withWidthSelector?: boolean;
	widthSelectorOptions?: string[];
	widthSelectorValue?: string | null;
	onChangeWidth?: (value: string) => void;
	defaultValues?: string[];
}> = ({ title, elements, onChange, initialValues, withWidthSelector = false, widthSelectorOptions = Object.values(EDrywallWidthCalculation), onChangeWidth, widthSelectorValue, defaultValues = [] }) => {
	const [editedElements, setEditedElements] = useState<LineItemElementProps[]>(
		elements.map((element, index) => {
			let selectedValue: string;

			if (element.type === "text") {
				selectedValue = element.value;
			} else {
				// Priority order: initialValues -> defaultValues (if exists in acceptableValues) -> element.defaultValue -> element.value or empty
				selectedValue =
					(index < initialValues.length && initialValues[index]) ||
					(defaultValues.find(defaultVal =>
						element.acceptableValues?.includes(defaultVal)
					)) ||
					element.defaultValue ||
					(element.allowEmptyValue ? "" : element.value);
			}

			return {
				...element,
				value: selectedValue,
			};
		}),
	);
	useEffect(() => {
		// make sure to call the onChange at least once to set the initial values in the rule
		setTimeout(() => {
			onChange(editedElements.map((e) => e.value));
		}, 100);
	}, []);

	return (
		<div className="border border-[#cfd4dc] rounded-2xl bg-white flex-col w-[48%]">
			<div className="p-1">{title}</div>
			<div className="justify-start items-center gap-1 flex bg-pink-100 p-1">
				{editedElements.map((element, index) => {
					if (element.type === "text") {
						return (
							<div
								key={`${element.type}-${index}-${element.value}`}
								className="rounded justify-start items-center flex whitespace-nowrap"
							>
								<div className="text-justify text-[#101828]  font-normal font-['Hanken Grotesk'] ">{element.value}</div>
							</div>
						);
					}
					const value = element.value.trim();
					if (value === "" || value === "$empty") return null;
					return (
						<div
							key={`${element.type}-${index}-${element.value}`}
							className="px-2 rounded justify-start items-center  whitespace-nowrap flex bg-[#eff8ff]"
						>
							<div className="text-justify text-[#101828] font-normal font-['Hanken Grotesk'] ">{value}</div>
						</div>
					);
				})}
				{withWidthSelector && (
					<div className="rounded justify-start items-center whitespace-nowrap flex bg-[#eff8ff]">
						<Select
							options={widthSelectorOptions.map((value) => ({ value, label: value }))}

							value={widthSelectorValue ? { value: widthSelectorValue, label: widthSelectorValue } : null}
							styles={customStyles}
							onChange={(e) => {
								onChangeWidth && onChangeWidth(e?.value || "");
							}}
							placeholder="Select width"
							className="w-[190px]"
						/>
					</div>
				)}
			</div>
			<div className="self-stretch h-[120px]  bg-white border-l border-r border-t border-[#cfd4dc] justify-between items-center inline-flex overflow-auto">
				{editedElements.map((element, onlyIndex) => {
					if (element.type === "text") return null;
					return (
						<div
							key={`${element.type}-${onlyIndex}-${element.value}`}
							className="grow shrink basis-0 self-stretch bg-white flex-col justify-start items-start gap-1 inline-flex border-r px-2"
						>
							{[...(element.acceptableValues ?? []), ...(element.allowEmptyValue ? ["$empty"] : [])]?.map((value, index) => {
								if (value !== element.value) {
									return (
										<div key={onlyIndex * 1000 + index} className="self-stretch justify-start items-start inline-flex">
											{/* <div className="w-6 h-6 relative" /> */}
											<div
												className="no-break grow shrink border border-black basis-0 text-[#475466] hover:text-[#1849a9] text-base font-normal font-['Hanken Grotesk'] leading-normal btn"
												onClick={() => {
													const newElements = [...editedElements];
													newElements[onlyIndex] = { ...newElements[onlyIndex], value };
													setEditedElements(newElements);
													onChange(newElements.map((e) => e.value));
												}}
											>
												{value === "$empty" ? "Empty value" : value}
											</div>
										</div>
									);
								} else {
									return (
										<div className="self-stretch pl-1  bg-[#eff8ff] rounded-lg border border-[#d1e9ff] justify-start items-center inline-flex">
											<div
												className="no-break grow shrink basis-0 text-[#1849a9] text-base font-bold font-['Hanken Grotesk'] leading-normal  btn"
												onClick={() => {
													const newElements = [...editedElements];
													newElements[onlyIndex] = { ...newElements[onlyIndex], value };
													setEditedElements(newElements);
													onChange(newElements.map((e) => e.value));
												}}
											>
												{value === "$empty" ? "Empty value" : value}
											</div>
										</div>
									);
								}
							})}
						</div>
					);
				})}
			</div>
		</div>
	);
};

function getLineItemFromElements(elements: LineItemElementProps[]) {
	// logDebug("getLineItemFromElements elements", elements);
	return elements
		.map((element) => ({ ...element, value: element.defaultValue || (element.allowEmptyValue ? "" : element.value) }))
		.map((element) => (element.value.trim() === "$empty" ? "" : element.value).trim())
		.join(" ");
}
function getLineItemFromElements2(elements: LineItemElementProps[]) {
	// logDebug("getLineItemFromElements elements", elements);
	return elements
		.map((element) => ({ ...element, value: element.value || (element.allowEmptyValue ? "" : element.defaultValue || "") }))
		.map((element) => (element.value.trim() === "$empty" ? "" : element.value).trim())
		.join(" ");
}

const SetQuantityPanel: React.FC<{
	elements: LineItemElementProps[];
	currentTupleId: string;
	onSelect?: (qnt: { quantity: number; measurement: string }) => void;
}> = ({ elements, onSelect, currentTupleId }) => {
	// logDebug('SetQuantityPanel elements', elements, getLineItemFromElements2(elements));
	const [quantity, setQuantity] = useState<string>("");
	const [result, setResult] = useState<{ val: number; measurement: string } | null>({ val: 0, measurement: "" });
	const [error, setError] = useState<string | null>(null);
	function checkQuantityAndSetResult() {
		try {
			const data = EngineUtils.verifyQuantity(quantity);
			if (data.number && data.text) {
				setQuantity(`${data.number} ${data.text}`);
			} else if (data.number && !data.text) {
				setQuantity(`${data.number} ${getMeasurementByTupleId(currentTupleId)}`);
			} else {
				//  if (EngineUtils.availableMeasurementsLow.includes(data.text.toLowerCase())) {
				//  }
			}
			setError(null);
			// TODO :: this is not complete
			onSelect &&
				onSelect({
					quantity: data.number,
					measurement: data.text,
				});
			logDebug("data", data);
		} catch (err: any) {
			logDebug("err", err);
			setError(err.message);
			setResult(null);
			return;
		}
	}

	return (
		<div className="h-[648px] py-4 flex-col justify-start items-center gap-2 inline-flex">
			<div className="self-stretch px-6 pt-5 pb-2 border-[#eaecf0] justify-between items-center inline-flex">
				<div className="text-justify text-[#0f1728] text-base font-semibold font-['Hanken Grotesk'] leading-snug">
					Please set the quantity
				</div>
			</div>
			<div className="self-stretch grow shrink basis-0 px-6 flex-col justify-start items-start gap-4 flex">
				<div className="self-stretch rounded-2xl border border-[#eaecf0] justify-start items-start inline-flex">
					<div className="grow shrink basis-0 self-stretch shadow flex-col justify-start items-start inline-flex">
						<div className="self-stretch h-12 px-6 bg-gray-50 border-l border-t border-b border-[#eaecf0] justify-between items-center inline-flex">
							<div className="self-stretch justify-start items-center flex">
								<div className="w-[376px] self-stretch pr-4 justify-start items-center gap-1 flex">
									<div className="w-4 h-4 pl-[5.14px] pr-[4.86px] pt-[3px] pb-1 opacity-0 justify-center items-center flex">
										<div className="w-1.5 h-[9px] relative flex-col justify-start items-start flex" />
									</div>
									<div className="grow shrink basis-0 text-[#0f1728] text-sm font-semibold font-['Hanken Grotesk'] leading-tight">
										Line Item
									</div>
								</div>
								<div className="w-[226px] self-stretch justify-start items-center gap-1 flex">
									<div className="w-4 h-4 pl-[5.14px] pr-[4.86px] pt-[3px] pb-1 opacity-0 justify-center items-center flex">
										<div className="w-1.5 h-[9px] relative flex-col justify-start items-start flex" />
									</div>
									<div className="grow shrink basis-0 text-[#0f1728] text-sm font-semibold font-['Hanken Grotesk'] leading-tight">
										Documented Quantity
									</div>
								</div>
							</div>
							<div className="h-10 opacity-0 justify-start items-start gap-4 flex">
								<div className="p-2.5 bg-white rounded-lg shadow border border-[#cfd4dc] justify-center items-center gap-2 flex">
									<div className="w-5 h-5 px-[2.50px] pt-[2.40px] pb-[3.33px] justify-center items-center flex" />
								</div>
								<div className="p-2.5 bg-[#d92c20] rounded-lg shadow border border-[#d92c20] justify-center items-center gap-2 flex">
									<div className="w-5 h-5 px-[2.50px] py-[1.67px] justify-center items-center flex" />
								</div>
							</div>
						</div>
						<div className="self-stretch h-[120px] px-6 bg-white border-l border-r border-b border-[#eaecf0] justify-between items-center inline-flex">
							<div className="grow shrink basis-0 self-stretch justify-start items-center flex">
								<div className="w-[376px] self-stretch pl-1.5 pr-4 justify-start items-center gap-4 flex">
									<div className="grow shrink basis-0 flex-col justify-start items-start gap-0.5 inline-flex">
										<div className="self-stretch h-10 flex-col justify-start items-start gap-1.5 flex">
											<div className="self-stretch pl-3.5 pr-2 py-2.5 rounded-lg justify-start items-center gap-2 inline-flex">
												<div className="grow shrink basis-0 h-5 justify-start items-center gap-2 flex">
													<div className="text-[#0f1728] text-sm font-normal font-['Hanken Grotesk'] leading-tight">
														{getLineItemFromElements2(elements)}
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div className="grow shrink basis-0 self-stretch pl-1.5 justify-start items-center flex">
									<div className="relative w-[187px] flex-col justify-start items-start gap-0.5 inline-flex">
										<div className="self-stretch h-8 flex-col justify-start items-start gap-1.5 flex">
											<div className="self-stretch pl-3.5 pr-2 py-1.5 bg-white rounded-lg border border-[#cfd4dc] justify-start items-center gap-2 inline-flex">
												<div className="grow shrink basis-0 h-5 justify-start items-center gap-2 flex">
													<input
														type="text"
														value={quantity}
														onChange={(e) => setQuantity(e.target.value)}
														onBlur={() => {
															checkQuantityAndSetResult();
														}}
														placeholder={`1 ${getMeasurementByTupleId(currentTupleId)}`}
														className="text-[#667084] text-sm font-normal font-['Hanken Grotesk'] leading-tight focus:outline-none"
													/>
												</div>
											</div>
										</div>
										<div className="absolute -bottom-5 text-sm text-red-400 no-break">{error}</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
const LineItemElement: React.FC<{
	elements: LineItemElementProps[];
	lineIndex: number;
	onSelect: (elements: LineItemElementProps[]) => void;
}> = ({ elements, lineIndex, onSelect }) => {
	return (
		<div
			className="overflow-x-auto w-full btn"
			onClick={() => {
				onSelect && onSelect(elements);
			}}
		>
			<div
				className={`self-stretch w-full h-20 ${lineIndex % 2 ? "bg-gray-50" : "bg-white"
					} border-l border-r border-b border-[#eaecf0] justify-between items-center inline-flex`}
			>
				<div className="grow shrink basis-0 self-stretch justify-start items-center flex">
					<div className="w-[691px] self-stretch px-8 justify-start items-center gap-2 flex ">
						{elements.map((element, index) => {
							if (element.type === "text") {
								return (
									<div
										key={`${element.type}-${index}-${element.value}`}
										className="rounded justify-start items-center flex whitespace-nowrap"
									>
										<div className="text-justify text-[#101828] text-lg font-normal font-['Hanken Grotesk'] ">
											{element.value}
										</div>
									</div>
								);
							}
							const value = (element.defaultValue || (element.allowEmptyValue ? "" : element.value)).trim();
							if (!value) return null;
							return (
								<div
									key={`${element.type}-${index}-${element.value}`}
									className="px-2 rounded justify-start items-center  whitespace-nowrap flex bg-[#eff8ff]"
								>
									<div className="text-justify text-[#101828] text-lg font-normal font-['Hanken Grotesk'] ">{value}</div>
								</div>
							);
						})}
					</div>
				</div>
			</div>
		</div>
	);
};

interface SelectOptionWithMeasurement extends SelectOption {
	measurement: string;
}
export function getMeasurementByTupleId(tid: string) {
	const cell = Object.values(TicType1CatToCellMap)
		.flat()
		.find((cell) => cell.value === tid);
	if (!cell) return "";
	return cell.measurement.includes("/") ? cell.measurement.split("/")[1] : cell.measurement;
}
// This was generated once in TicSheetType1._setupCellIdIfNeeded();
// TODO :: we need to implement more general solution for this
export const TicType1CatToCellMap: { [key: string]: SelectOptionWithMeasurement[] } = {
	Contents: [
		{
			label: "Protect Contents with plastic",
			value: "contents_protect_contents_with_plastic",
			measurement: "SF",
		},
		{
			label: "Manipulation (Room Size) (Hours)",
			value: "contents_manipulation_room_size_hours",
			measurement: "Size/Hrs",
		},
		{
			label: "Pack Out (YES)",
			value: "contents_pack_out_yes",
			measurement: "see pack out tic sheet",
		},
		{
			label: "Boxes (S) (M) (L)",
			value: "contents_boxes_s_m_l",
			measurement: "EA",
		},
		{
			label: "Bubble Wrap",
			value: "contents_bubble_wrap",
			measurement: "RL",
		},
		{
			label: "Window Drapes (detach/reset)",
			value: "contents_window_drapes_detach_reset",
			measurement: "EA",
		},
		{
			label: "Window Blind detach & reset",
			value: "contents_window_blind_detach_and_reset",
			measurement: "EA",
		},
	],
	"PAINT & STAIN": [
		{
			label: "Masking (SF) (LF)",
			value: "paint_and_stain_masking_sf_lf",
			measurement: "SF/LF",
		},
		{
			label: "Seal/Prime",
			value: "paint_and_stain_seal_prime",
			measurement: "SF",
		},
		{
			label: "Walls",
			value: "paint_and_stain_walls",
			measurement: "SF",
		},
		{
			label: "Ceiling",
			value: "paint_and_stain_ceiling",
			measurement: "SF",
		},
		{
			label: "Baseboards",
			value: "paint_and_stain_baseboards",
			measurement: "LF",
		},
		{
			label: "Crown Molding",
			value: "paint_and_stain_crown_molding",
			measurement: "LF",
		},
		{
			label: "Chair Rail",
			value: "paint_and_stain_chair_rail",
			measurement: "LF",
		},
		{
			label: "Door Slab (Sides)",
			value: "paint_and_stain_door_slab_sides",
			measurement: "EA",
		},
		{
			label: "Opening (window or door)",
			value: "paint_and_stain_opening_window_or_door",
			measurement: "EA",
		},
		{
			label: "Opening (window or door) (Large)",
			value: "paint_and_stain_opening_window_or_door_large",
			measurement: "EA",
		},
		{
			label: "Concrete (Floor) (Wall)",
			value: "paint_and_stain_concrete_floor_wall",
			measurement: "SF",
		},
		{
			label: "Shelving",
			value: "paint_and_stain_shelving",
			measurement: "LF",
		},
		{
			label: "Casing",
			value: "paint_and_stain_casing",
			measurement: "LF",
		},
		{
			label: "Paneling",
			value: "paint_and_stain_paneling",
			measurement: "SF",
		},
	],
	ELECTRICAL: [
		{
			label: "Light Fixture",
			value: "electrical_light_fixture",
			measurement: "EA",
		},
		{
			label: "Recessed Light Fixture",
			value: "electrical_recessed_light_fixture",
			measurement: "EA",
		},
		{
			label: "Fluorescent Light",
			value: "electrical_fluorescent_light",
			measurement: "EA",
		},
		{
			label: "Hanging Light Fixture",
			value: "electrical_hanging_light_fixture",
			measurement: "EA",
		},
		{
			label: "Chandelier",
			value: "electrical_chandelier",
			measurement: "EA",
		},
		{
			label: "Wall Sconce",
			value: "electrical_wall_sconce",
			measurement: "EA",
		},
		{
			label: "Ceiling Fan",
			value: "electrical_ceiling_fan",
			measurement: "EA",
		},
		{
			label: "Wall Mounted TV",
			value: "electrical_wall_mounted_tv",
			measurement: "EA",
		},
		{
			label: "Smoke / CO Detector",
			value: "electrical_smoke_co_detector",
			measurement: "EA",
		},
		{
			label: "Door Chime",
			value: "electrical_door_chime",
			measurement: "EA",
		},
		{
			label: "Baseboard Electric Heater",
			value: "electrical_baseboard_electric_heater",
			measurement: "LF",
		},
		{
			label: "Wall Heater",
			value: "electrical_wall_heater",
			measurement: "EA",
		},
		{
			label: "Phone, TV or speaker outlet",
			value: "electrical_phone_tv_or_speaker_outlet",
			measurement: "EA",
		},
		{
			label: "Porcelain Light Fixture",
			value: "electrical_porcelain_light_fixture",
			measurement: "EA",
		},
		{
			label: "110 Volt Wiring Run",
			value: "electrical_110_volt_wiring_run",
			measurement: "EA",
		},
		{
			label: "Rewire",
			value: "electrical_rewire",
			measurement: "SF",
		},
		{
			label: "GFI Interrupter",
			value: "electrical_gfi_interrupter",
			measurement: "EA",
		},
		{
			label: "Outlet / Switch Covers",
			value: "electrical_outlet_switch_covers",
			measurement: "EA",
		},
		{
			label: "Outlet / Switch",
			value: "electrical_outlet_switch",
			measurement: "EA",
		},
		{
			label: "Check Circuit",
			value: "electrical_check_circuit",
			measurement: "EA",
		},
		{
			label: "Megohmmeter Check",
			value: "electrical_megohmmeter_check",
			measurement: "EA",
		},
	],
	"WALLS & CEILING": [
		{
			label: "Wall Insulation (R-13) , (R-15), (R-19)",
			value: "walls_and_ceiling_wall_insulation_r_13_r_15_r_19",
			measurement: "SF",
		},
		{
			label: "Ceiling Insulation (R-30) (R-38) ( )",
			value: "walls_and_ceiling_ceiling_insulation_r_30_r_38",
			measurement: "SF",
		},
		{
			label: "Drywall LF 1/2 or 5/8 (flood cuts) 2ft or 4ft",
			value: "walls_and_ceiling_drywall_lf_1_2_or_5_8_flood_cuts_2ft_or_4ft",
			measurement: "LF",
		},
		{
			label: "Drywall - 1/2 or 5/8",
			value: "walls_and_ceiling_drywall_1_2_or_5_8",
			measurement: "SF",
		},
		{
			label: "Drywall Patch",
			value: "walls_and_ceiling_drywall_patch",
			measurement: "EA",
		},
		{
			label: "Drywall tape joint/repair - per LF",
			value: "walls_and_ceiling_drywall_tape_joint_repair_per_lf",
			measurement: "LF",
		},
		{
			label: "Cement Board ( 1/4 ) or (1/2)",
			value: "walls_and_ceiling_cement_board_1_4_or_1_2",
			measurement: "SF",
		},
		{
			label: "Plaster >Coats (1) (2) (3) or Blueboard",
			value: "walls_and_ceiling_plaster_coats_1_2_3_or_blueboard",
			measurement: "SF",
		},
		{
			label: "Plaster Patch",
			value: "walls_and_ceiling_plaster_patch",
			measurement: "EA",
		},
		{
			label: "Furring Strip",
			value: "walls_and_ceiling_furring_strip",
			measurement: "SF",
		},
		{
			label: "Ceiling Tile (acoustic)/ (suspended) (2x2) (2×4)",
			value: "walls_and_ceiling_ceiling_tile_acoustic_suspended_2x2_2_4",
			measurement: "SF",
		},
		{
			label: "Ceiling grid (2x2) or (2x4)",
			value: "walls_and_ceiling_ceiling_grid_2x2_or_2x4",
			measurement: "SF",
		},
		{
			label: "Ceramic Tile - Wall",
			value: "walls_and_ceiling_ceramic_tile_wall",
			measurement: "SF",
		},
		{
			label: "Tile Feature Strip",
			value: "walls_and_ceiling_tile_feature_strip",
			measurement: "LF",
		},
		{
			label: "Tile bullnose",
			value: "walls_and_ceiling_tile_bullnose",
			measurement: "LF",
		},
		{
			label: "Skim Coat (light) (regular) (heavy)",
			value: "walls_and_ceiling_skim_coat_light_regular_heavy",
			measurement: "SF",
		},
		{
			label: "Texture Finish",
			value: "walls_and_ceiling_texture_finish",
			measurement: "SF",
		},
		{
			label: "Paneling - (Size)=",
			value: "walls_and_ceiling_paneling_size_",
			measurement: "SF",
		},
	],
	"FINISH TRIM": [
		{
			label: "Baseboards (Size) =",
			value: "finish_trim_baseboards_size_",
			measurement: "LF",
		},
		{
			label: "Base shoe",
			value: "finish_trim_base_shoe",
			measurement: "LF",
		},
		{
			label: "Crown Molding (Size)=",
			value: "finish_trim_crown_molding_size_",
			measurement: "LF",
		},
		{
			label: "Cove Molding",
			value: "finish_trim_cove_molding",
			measurement: "LF",
		},
		{
			label: "Quarter Round",
			value: "finish_trim_quarter_round",
			measurement: "LF",
		},
		{
			label: "Chair Rail (Size)=",
			value: "finish_trim_chair_rail_size_",
			measurement: "LF",
		},
		{
			label: "Casing (Size)=",
			value: "finish_trim_casing_size_",
			measurement: "LF",
		},
		{
			label: "Window Trim (casing & stop)",
			value: "finish_trim_window_trim_casing_and_stop",
			measurement: "LF",
		},
		{
			label: "Window Stool & Apron",
			value: "finish_trim_window_stool_and_apron",
			measurement: "LF",
		},
		{
			label: "Window Sill",
			value: "finish_trim_window_sill",
			measurement: "LF",
		},
		{
			label: "Window Opening",
			value: "finish_trim_window_opening",
			measurement: "LF",
		},
		{
			label: "Vinyl Base Molding (4 inch) (6 inch)",
			value: "finish_trim_vinyl_base_molding_4_inch_6_inch",
			measurement: "LF",
		},
		{
			label: "Trim Board (size)",
			value: "finish_trim_trim_board_size",
			measurement: "LF",
		},
	],
	"DOORS & WINDOWS": [
		{
			label: "Bi-Fold Door Unit",
			value: "doors_and_windows_bi_fold_door_unit",
			measurement: "EA",
		},
		{
			label: "Int. Pre-hung Door (type)",
			value: "doors_and_windows_int_pre_hung_door_type",
			measurement: "EA",
		},
		{
			label: "Ext. Pre-hung Door (type)",
			value: "doors_and_windows_ext_pre_hung_door_type",
			measurement: "EA",
		},
		{
			label: "Side Lite",
			value: "doors_and_windows_side_lite",
			measurement: "LF",
		},
		{
			label: "Door Lockset",
			value: "doors_and_windows_door_lockset",
			measurement: "EA",
		},
		{
			label: "Retrofit Door",
			value: "doors_and_windows_retrofit_door",
			measurement: "EA",
		},
		{
			label: "Door Slab (type)",
			value: "doors_and_windows_door_slab_type",
			measurement: "EA",
		},
		{
			label: "Window: (wood) (Vinyl) Style=",
			value: "doors_and_windows_window_wood_vinyl_style_",
			measurement: "SF",
		},
		{
			label: "Retrofit Window",
			value: "doors_and_windows_retrofit_window",
			measurement: "EA",
		},
		{
			label: "Bay or Bow Window",
			value: "doors_and_windows_bay_or_bow_window",
			measurement: "LF",
		},
	],
	PLUMBING: [
		{
			label: "Radiator Unit / Size",
			value: "plumbing_radiator_unit_size",
			measurement: "EA",
		},
		{
			label: "Radiator Cover / Size",
			value: "plumbing_radiator_cover_size",
			measurement: "EA",
		},
		{
			label: "Wall Heater",
			value: "plumbing_wall_heater",
			measurement: "EA",
		},
		{
			label: "Baseboard Heater",
			value: "plumbing_baseboard_heater",
			measurement: "LF",
		},
	],
	Shelving: [
		{
			label: "Shelving - Wood or Wire",
			value: "shelving_shelving_wood_or_wire",
			measurement: "LF",
		},
		{
			label: "Melamine Organizer",
			value: "shelving_melamine_organizer",
			measurement: "LF",
		},
		{
			label: "Closet Shelf & Rod",
			value: "shelving_closet_shelf_and_rod",
			measurement: "LF",
		},
	],
	"Simulated Header": [
		{
			label: "Debris > PickUp, Tandem Dump, Dump Truck, Dumpster (12) (20) (30) (40)",
			value: "simulated_header_debris_pickup_tandem_dump_dump_truck_dumpster_12_20_30_40",
			measurement: "",
		},
		{
			label: "(Program Work) (Adjuster Direct) (Private)",
			value: "simulated_header_program_work_adjuster_direct_private",
			measurement: "",
		},
	],
	FLOORING: [
		{
			label: "Carpet",
			value: "flooring_carpet",
			measurement: "SF",
		},
		{
			label: "Wood Flooring (HW) (ENG) (LAM)",
			value: "flooring_wood_flooring_hw_eng_lam",
			measurement: "SF",
		},
		{
			label: "Vinyl Sheet (roll)",
			value: "flooring_vinyl_sheet_roll",
			measurement: "SF",
		},
		{
			label: "Carpet Pad",
			value: "flooring_carpet_pad",
			measurement: "SF",
		},
		{
			label: "Sand, Stain & Finish Wood Floor",
			value: "flooring_sand_stain_and_finish_wood_floor",
			measurement: "SF",
		},
		{
			label: "Vinyl Tile",
			value: "flooring_vinyl_tile",
			measurement: "SF",
		},
		{
			label: "add for Berber/pattern",
			value: "flooring_add_for_berber_pattern",
			measurement: "(Yes)",
		},
		{
			label: "Add for Diagonal Installation",
			value: "flooring_add_for_diagonal_installation",
			measurement: "SF",
		},
		{
			label: "Vinyl (self adhesive)",
			value: "flooring_vinyl_self_adhesive",
			measurement: "SF",
		},
		{
			label: 'Step charge for "tucked" (per rise)',
			value: "flooring_step_charge_for_tucked_per_rise",
			measurement: "EA",
		},
		{
			label: "Dustless Sanding",
			value: "flooring_dustless_sanding",
			measurement: "(Yes)",
		},
		{
			label: "Ceramic Tile",
			value: "flooring_ceramic_tile",
			measurement: "SF",
		},
		{
			label: "Glue Down Carpet",
			value: "flooring_glue_down_carpet",
			measurement: "SF",
		},
		{
			label: "Wood Floor Border Inlay",
			value: "flooring_wood_floor_border_inlay",
			measurement: "LF",
		},
		{
			label: "Seal Tile",
			value: "flooring_seal_tile",
			measurement: "SF",
		},
		{
			label: "Prep Floor",
			value: "flooring_prep_floor",
			measurement: "SF",
		},
		{
			label: "Vapor Barrier",
			value: "flooring_vapor_barrier",
			measurement: "SF",
		},
		{
			label: "Re-grout tile",
			value: "flooring_re_grout_tile",
			measurement: "SF",
		},
		{
			label: "Transition Strip",
			value: "flooring_transition_strip",
			measurement: "LF",
		},
		{
			label: "Underlayment / Subfloor",
			value: "flooring_underlayment_subfloor",
			measurement: "SF",
		},
		{
			label: "Cement Board ( 1/4) or (1/2)",
			value: "flooring_cement_board_1_4_or_1_2",
			measurement: "SF",
		},
		{
			label: "Threshold:(wood) (MTL) (cult marble) (marble)",
			value: "flooring_threshold_wood_mtl_cult_marble_marble",
			measurement: "LF",
		},
		{
			label: "Level Concrete (Light) (Avg) (Heavy)",
			value: "flooring_level_concrete_light_avg_heavy",
			measurement: "SF",
		},
		{
			label: "Add to remove GLUE",
			value: "flooring_add_to_remove_glue",
			measurement: "SF",
		},
	],
};
export default LineEditingDialog;
